/* CFO Unified Dashboard — Patch 14 + Patch 24 live refresh */
import React, { useState, useCallback, useEffect } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { Loader2, RefreshCw, TrendingUp, TrendingDown, AlertTriangle, ShieldCheck, CheckCircle2, XCircle } from 'lucide-react';
import UnifiedActionQueueWidget from '../components/UnifiedActionQueueWidget';
import ExecutiveNarrativeCard from '../components/ExecutiveNarrativeCard';
import VATFilingDeadlineCard from '../components/VATFilingDeadlineCard';
import useActionQueueSocket from '../hooks/useActionQueueSocket';

const fmt = (n) => (Number(n) || 0).toLocaleString('en-US',
  { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const today = new Date();
const defaultPeriod = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;

export default function CFODashboard() {
  const [period, setPeriod] = useState(defaultPeriod);
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axiosInstance.get(`/cfo-dashboard-v2/dashboard?period=${period}`);
      setData(r.data);
    } catch (e) {
      toast.error(`Failed: ${e.response?.data?.detail || e.message}`);
    } finally { setLoading(false); }
  }, [period]);

  useEffect(() => { load(); }, [load]);

  // Patch 24 — pull lastEvent from the live socket so the dashboard's
  // own data (close confidence + financial truth) refreshes whenever
  // a high-impact event lands (block, reconciliation change, approval,
  // overhead allocation). Lightweight: just calls the existing loader.
  const { lastEvent } = useActionQueueSocket();
  useEffect(() => {
    if (!lastEvent) return;
    const t = lastEvent.event_type;
    if (t === 'posting.blocked' ||
        t === 'reconciliation.changed' ||
        t === 'overhead.allocated' ||
        t === 'quotation.approved') {
      load();
    }
  }, [lastEvent, load]);

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="cfo-dashboard-page">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">🧠 CFO Unified Dashboard</h1>
          <p className="text-sm text-slate-600">Financial Truth — One screen, every number</p>
        </div>
        <div className="flex items-center gap-2">
          <a
            href="/governance-analytics"
            data-testid="cfo-governance-link"
            className="hidden md:inline-flex items-center gap-2 bg-slate-100 hover:bg-slate-200 text-slate-900 rounded px-3 py-1.5 text-sm font-semibold border border-slate-300"
          >
            <ShieldCheck size={14} /> Governance Analytics
          </a>
          <input
            data-testid="cfo-period-input"
            type="text"
            value={period}
            onChange={e => setPeriod(e.target.value)}
            placeholder="2026-06"
            className="border border-slate-300 rounded px-3 py-1.5 text-sm font-mono w-28"
          />
          <button
            data-testid="cfo-refresh-btn"
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white rounded px-4 py-1.5 text-sm font-semibold disabled:opacity-50"
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            Refresh
          </button>
        </div>
      </header>

      {data && (
        <>
          {/* VAT Filing Deadline — surfaces ZATCA submission window
              (30 days post period-end). Placed at the top of the CFO
              workspace so finance leadership sees it before reading
              the close-confidence banner. Self-contained. */}
          <VATFilingDeadlineCard />

          {/* Patch 24.5 — AI Executive Narrative card. Reads from the
              same persisted row that the daily email + board pack PDF
              consume, so the dashboard, email and PDF never disagree. */}
          <ExecutiveNarrativeCard />

          <div className={`rounded-lg p-4 border-2 ${
            data.close_confidence.label === 'HIGH'   ? 'bg-emerald-50 border-emerald-300' :
            data.close_confidence.label === 'MEDIUM' ? 'bg-amber-50 border-amber-300' :
                                                       'bg-rose-50 border-rose-300'
          }`} data-testid="close-confidence-banner">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs uppercase font-semibold opacity-70">Close Confidence Score</div>
                <div className="text-4xl font-bold mt-1">{data.close_confidence.score}<span className="text-xl opacity-50">/100</span></div>
                <div className="text-sm font-semibold mt-1">{data.close_confidence.label}</div>
              </div>
              <div className="text-xs text-right max-w-md space-y-0.5">
                {data.close_confidence.risks.length === 0 ? (
                  <div className="text-emerald-700">✓ No risks detected</div>
                ) : (
                  data.close_confidence.risks.map((r, i) => (
                    <div key={i} className="flex items-center gap-1 justify-end">
                      <AlertTriangle size={10} /> {r}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Patch 19 — Unified Action Queue Widget. Aggregates every
              actionable Decision Center (Repairs, Reconciliation, VOs,
              Leads, RFQs, Data Quality) so the CFO never has to crawl
              six pages to discover where attention is required. */}
          <UnifiedActionQueueWidget />

          <FinancialTruthPanel truth={data.financial_truth_status} />
          <FinancialIntegrityKpis kpis={data.financial_integrity_kpis} />

          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            {[
              { lbl: 'Revenue (41xxx)',         val: data.company_pl.revenue,         cls: 'bg-emerald-50 text-emerald-900 border-emerald-200' },
              { lbl: 'Direct Cost (51xxx)',     val: data.company_pl.direct_cost_51,  cls: 'bg-amber-50    text-amber-900    border-amber-200'    },
              { lbl: 'G&A Unallocated (52)',    val: data.company_pl.unallocated_52,  cls: 'bg-orange-50  text-orange-900  border-orange-200'  },
              { lbl: 'Allocated OH (53)',       val: data.company_pl.allocated_oh_53, cls: 'bg-rose-50    text-rose-900    border-rose-200'    },
              { lbl: 'Net Profit',              val: data.company_pl.net_profit,      cls: 'bg-indigo-50  text-indigo-900  border-indigo-200'  },
            ].map((c, i) => (
              <div key={i} data-testid={`cfo-pl-${i}`} className={`rounded-lg p-3 border ${c.cls}`}>
                <div className="text-[10px] uppercase tracking-wider opacity-70">{c.lbl}</div>
                <div className="text-xl font-bold mt-1 font-mono">{fmt(c.val)}</div>
                {i === 4 && (
                  <div className="text-[11px] mt-0.5">Margin: {data.company_pl.net_margin_pct}%</div>
                )}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="bg-white border border-slate-200 rounded-lg p-4">
              <h3 className="text-sm font-bold text-slate-900 mb-2">📊 Overhead Absorption</h3>
              <div className="space-y-1 text-xs">
                <div className="flex justify-between"><span>Total G&A Pool</span><span className="font-mono font-bold">{fmt(data.overhead_absorption.total_pool)} SAR</span></div>
                <div className="flex justify-between text-rose-700"><span>Unallocated (52)</span><span className="font-mono">{fmt(data.overhead_absorption.pool_unallocated_52)} SAR</span></div>
                <div className="flex justify-between text-emerald-700"><span>Allocated (53)</span><span className="font-mono">{fmt(data.overhead_absorption.allocated_to_projects_53)} SAR</span></div>
                <div className="mt-2">
                  <div className="w-full bg-slate-100 rounded h-3 mt-1 overflow-hidden">
                    <div className="bg-emerald-500 h-full" style={{ width: `${data.overhead_absorption.absorption_rate_pct}%` }} />
                  </div>
                  <div className="text-right text-xs font-bold mt-0.5">{data.overhead_absorption.absorption_rate_pct}% absorption</div>
                </div>
              </div>
            </div>
            <div className="bg-white border border-slate-200 rounded-lg p-4">
              <h3 className="text-sm font-bold text-slate-900 mb-2">🏛 VAT Exposure</h3>
              <div className="space-y-1 text-xs">
                <div className="flex justify-between"><span>Input VAT (Recoverable)</span><span className="font-mono text-emerald-700">{fmt(data.vat_exposure.vat_input_recoverable)}</span></div>
                <div className="flex justify-between"><span>Output VAT (Payable)</span><span className="font-mono text-rose-700">{fmt(data.vat_exposure.vat_output_payable)}</span></div>
                <div className="flex justify-between font-bold border-t border-slate-200 pt-1 mt-1">
                  <span>Net VAT Position</span>
                  <span className={`font-mono ${data.vat_exposure.net_vat_position > 0 ? 'text-rose-700' : 'text-emerald-700'}`}>
                    {fmt(data.vat_exposure.net_vat_position)} ({data.vat_exposure.status_hint})
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <RankCard title="🏆 Top 5 by Margin" icon={<TrendingUp size={14} className="text-emerald-600" />} rows={data.projects.top_by_margin} />
            <RankCard title="🚨 Bottom 5 by Margin" icon={<TrendingDown size={14} className="text-rose-600" />} rows={data.projects.bottom_by_margin} />
          </div>

          <div className="bg-white border border-slate-200 rounded-lg p-4">
            <h3 className="text-sm font-bold text-slate-900 mb-2">
              ⚡ Pending CFO Decisions ({data.cfo_pending_decisions.length})
            </h3>
            {data.cfo_pending_decisions.length === 0 ? (
              <p className="text-xs text-slate-500">No drafts awaiting decision. ✓</p>
            ) : (
              <table className="w-full text-xs font-mono">
                <thead><tr className="bg-slate-50 text-left">
                  <th className="px-2 py-1">Label</th><th className="px-2 py-1">Period</th>
                  <th className="px-2 py-1">Basis</th><th className="px-2 py-1 text-right">Allocated</th>
                  <th className="px-2 py-1">Status</th>
                </tr></thead>
                <tbody>
                  {data.cfo_pending_decisions.map((p) => (
                    <tr key={p.id} data-testid={`pending-row-${p.id}`}>
                      <td className="px-2 py-1">{p.label}</td>
                      <td className="px-2 py-1">{p.period}</td>
                      <td className="px-2 py-1">{p.basis}</td>
                      <td className="px-2 py-1 text-right">{fmt(p.allocated_total)}</td>
                      <td className="px-2 py-1">
                        <span className={`px-1.5 py-0.5 rounded text-[10px] ${p.status === 'draft' ? 'bg-slate-100' : 'bg-sky-100 text-sky-800'}`}>{p.status}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <div className="mt-2 text-xs text-slate-500">
              Manage on the <a href="/overhead-report" className="text-sky-700 underline">Overhead Simulator</a>.
            </div>
          </div>

          {data.recent_postings.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-lg p-4">
              <h3 className="text-sm font-bold text-slate-900 mb-2">📜 Recent Postings</h3>
              <table className="w-full text-xs font-mono">
                <thead><tr className="bg-slate-50 text-left">
                  <th className="px-2 py-1">Label</th><th className="px-2 py-1">Journal</th>
                  <th className="px-2 py-1 text-right">Allocated</th><th className="px-2 py-1">By</th>
                  <th className="px-2 py-1">Status</th>
                </tr></thead>
                <tbody>
                  {data.recent_postings.map((p) => (
                    <tr key={p.id}>
                      <td className="px-2 py-1">{p.label}</td>
                      <td className="px-2 py-1">{p.journal_no}{p.reversal_journal_no && <div className="text-rose-600">↩ {p.reversal_journal_no}</div>}</td>
                      <td className="px-2 py-1 text-right">{fmt(p.allocated_total)}</td>
                      <td className="px-2 py-1">{p.posted_by_name}</td>
                      <td className="px-2 py-1">
                        <span className={`px-1.5 py-0.5 rounded text-[10px] ${p.status === 'posted' ? 'bg-emerald-100 text-emerald-800' : 'bg-rose-100 text-rose-800'}`}>{p.status}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
      {!data && !loading && (
        <div className="text-center text-slate-500 text-sm py-12 border border-dashed border-slate-300 rounded-lg">
          Pick a period and click <strong>Refresh</strong>.
        </div>
      )}
    </div>
  );
}

function RankCard({ title, icon, rows }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4">
      <h3 className="text-sm font-bold text-slate-900 mb-2 inline-flex items-center gap-1">{icon}{title}</h3>
      {!rows?.length ? (
        <p className="text-xs text-slate-500">No projects.</p>
      ) : (
        <table className="w-full text-xs font-mono">
          <thead><tr className="text-slate-500"><th className="text-left">Project</th><th className="text-right">Profit</th><th className="text-right">Margin</th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.project_id}>
                <td>{r.project_name || r.project_code || r.project_id?.slice(0,8)}</td>
                <td className="text-right">{fmt(r.profit)}</td>
                <td className={`text-right font-bold ${r.margin_pct < 0 ? 'text-rose-700' : 'text-emerald-700'}`}>{r.margin_pct?.toFixed(1)}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function FinancialIntegrityKpis({ kpis }) {
  if (!kpis || kpis.verdict === 'UNKNOWN') return null;
  const VERDICT = {
    CLEAN:       { cls: 'bg-emerald-50 border-emerald-300', text: 'text-emerald-800' },
    WARNING:     { cls: 'bg-amber-50 border-amber-300',     text: 'text-amber-900'   },
    INVESTIGATE: { cls: 'bg-rose-50 border-rose-300',       text: 'text-rose-900'    },
  };
  const v = VERDICT[kpis.verdict] || VERDICT.CLEAN;
  const cells = [
    { lbl: 'Invoices Without Journal', val: kpis.invoices_without_journal },
    { lbl: 'Payments Without Journal', val: kpis.payments_without_journal },
    { lbl: 'Bills Without Journal',    val: kpis.bills_without_journal },
    { lbl: 'Orphan Cost Lines (51/53)',val: kpis.orphan_project_cost_lines },
    { lbl: 'VAT Exceptions',           val: kpis.vat_exceptions },
  ];
  return (
    <div className={`rounded-lg p-4 border-2 ${v.cls}`} data-testid="financial-integrity-kpis">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-xs uppercase font-semibold opacity-70">Financial Integrity Signals</div>
          <div className={`text-sm font-bold ${v.text}`}>
            {kpis.verdict} — {kpis.total_signals} total signal{kpis.total_signals === 1 ? '' : 's'}
          </div>
        </div>
        <a
          href="/reconciliation-center"
          data-testid="open-investigation-from-kpis"
          className="text-xs underline text-slate-700 hover:text-slate-900"
        >
          Investigate →
        </a>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        {cells.map((c, i) => (
          <div
            key={i}
            data-testid={`integrity-kpi-${i}`}
            className={`bg-white/70 rounded p-2.5 border ${c.val > 0 ? 'border-rose-200' : 'border-emerald-200'}`}
          >
            <div className="text-[10px] uppercase opacity-70 tracking-wider">{c.lbl}</div>
            <div className={`text-2xl font-bold font-mono mt-0.5 ${c.val > 0 ? 'text-rose-700' : 'text-emerald-700'}`}>
              {c.val}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function FinancialTruthPanel({ truth }) {
  if (!truth || !truth.checks || truth.checks.length === 0) return null;
  const VERDICT = {
    CLEAN:       { cls: 'bg-emerald-50 border-emerald-300', text: 'text-emerald-800', label: 'CLEAN' },
    WARNING:     { cls: 'bg-amber-50 border-amber-300',     text: 'text-amber-900',   label: 'WARNING' },
    INVESTIGATE: { cls: 'bg-rose-50 border-rose-300',       text: 'text-rose-900',    label: 'INVESTIGATE' },
    UNKNOWN:     { cls: 'bg-slate-50 border-slate-300',     text: 'text-slate-700',   label: 'UNKNOWN' },
  };
  const STATUS = {
    PASS:    { Icon: CheckCircle2,  cls: 'text-emerald-600' },
    WARNING: { Icon: AlertTriangle, cls: 'text-amber-600'   },
    FAIL:    { Icon: XCircle,       cls: 'text-rose-600'    },
  };
  const v = VERDICT[truth.verdict] || VERDICT.UNKNOWN;
  return (
    <div
      className={`rounded-lg p-4 border-2 ${v.cls}`}
      data-testid="financial-truth-panel"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="inline-flex items-center gap-2">
          <ShieldCheck size={18} className={v.text} />
          <div>
            <div className="text-xs uppercase font-semibold opacity-70">Financial Truth Status</div>
            <div className={`text-sm font-bold ${v.text}`}>
              {v.label} — Integrity Score {truth.integrity_score ?? '—'}%
            </div>
          </div>
        </div>
        <a
          href="/reconciliation-center"
          data-testid="open-reconciliation-center-link"
          className="text-xs underline text-slate-700 hover:text-slate-900"
        >
          Open Reconciliation Center →
        </a>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
        {truth.checks.map((c, i) => {
          const s = STATUS[c.status] || STATUS.PASS;
          const Icon = s.Icon;
          return (
            <div
              key={i}
              data-testid={`truth-check-${i}`}
              className="flex items-center justify-between gap-2 bg-white/70 rounded px-2.5 py-1.5 border border-white/80"
            >
              <div className="flex items-center gap-1.5 min-w-0">
                <Icon size={14} className={`${s.cls} shrink-0`} />
                <div className="text-[11px] font-medium truncate">{c.name}</div>
              </div>
              <div className={`text-[10px] font-bold ${s.cls}`}>{c.status}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
